/* eslint-disable no-case-declarations */

import {
  AIStore,
  Formula,
  FormulaField,
  FormulaFields,
  FormulaFieldType,
  FormulaFieldValue,
  Instruments,
  Key,
  KeyRoot,
  KeyQuality,
} from 'services/store/ai/types';

const fields = Object.values(FormulaFields);

const parseInstruments = (param: Array<string>): Instruments['value'] =>
  param.reduce<Instruments['value']>((acc, item) => {
    const [instrument, countParam] = item.split(':');
    const count = countParam ? parseInt(countParam, 10) : 1;
    return {
      ...acc,
      [instrument]: count,
    };
  }, {});

const parseKey = (param: string): Key['value'] | null => {
  const [root, quality] = param.split('-');
  const rootIsValid = Object.values<string>(KeyRoot).includes(root);
  const qualityIsValid = Object.values<string>(KeyQuality).includes(quality);

  if (qualityIsValid && rootIsValid) {
    return { quality: quality as KeyQuality, root: root as KeyRoot };
  }

  return null;
};

const parseNumber = (param: string) => {
  const value = parseInt(param, 10);
  if (Number.isNaN(value)) {
    return null;
  }
  return value;
};

export const getFormulaValue = (
  queryParams: URLSearchParams,
  field: FormulaFieldType
): FormulaFieldValue | null => {
  const queryValue = queryParams.get(field);
  if (!queryValue) {
    return null;
  }
  switch (field) {
    case FormulaFields.Bpm:
    case FormulaFields.Duration:
    case FormulaFields.Structure:
      return parseNumber(queryValue);
    case FormulaFields.Instruments:
      return parseInstruments(queryParams.getAll(field));
    case FormulaFields.Key:
      return parseKey(queryValue);
    default:
      return queryValue;
  }
};

interface ParsedFormula {
  primaryGenre: AIStore['primaryGenre'];
  microGenre: AIStore['microGenre'];
  formula: Formula;
}

export const parseFormula = (queryString: string): ParsedFormula => {
  const queryParams = new URLSearchParams(queryString);

  const primaryGenre = queryParams.get('primary_genre');
  const microGenre = queryParams.get('micro_genre');

  const formula = fields.reduce<Formula>((acc, field) => {
    const value = getFormulaValue(queryParams, field);

    if (value === null) {
      return acc;
    }

    return [
      ...acc,
      {
        field,
        value,
      } as FormulaField,
    ];
  }, []);

  return { primaryGenre, microGenre, formula };
};

export const appendInstrumentsToParams = (
  instruments: Instruments['value'],
  queryParams: URLSearchParams
) => {
  Object.keys(instruments).forEach((instrument) => {
    const count = instruments[instrument as keyof Instruments['value']];
    if (!count) {
      return;
    }
    queryParams.append('instruments', `${instrument}:${count}`);
  });
};

interface GenerateFormulaLinkProps {
  primaryGenre: AIStore['primaryGenre'];
  microGenre: AIStore['microGenre'];
  formula: Formula;
}

export const generateFormulaSearchParams = ({
  primaryGenre,
  microGenre,
  formula,
}: GenerateFormulaLinkProps): URLSearchParams => {
  const queryParams = new URLSearchParams();

  if (primaryGenre) {
    queryParams.append('primary_genre', primaryGenre);
  }

  if (microGenre) {
    queryParams.append('micro_genre', microGenre);
  }

  formula.forEach(({ field, value }) => {
    if (value === null) {
      return;
    }
    switch (field) {
      case FormulaFields.UserLoop:
        return;
      case FormulaFields.Instruments:
        appendInstrumentsToParams(value, queryParams);
        return;
      case FormulaFields.Key:
        const { root, quality } = value;
        queryParams.append(field, [root, quality].join('-'));
        return;
      default:
        queryParams.append(field, value.toString());
    }
  });

  return queryParams;
};
